import React, { useEffect, useRef, useState } from 'react';
import XSmall from '@uber/base-icons/x-small';
import { styled } from 'baseui';
import { Notification } from 'baseui/notification';
import { document } from 'global';
import { type RGBColor } from '../constants/colors';
import { type IntentT } from '../intent-list/intent-list';
import { getInitialTextMap, labelText, removeIntentAndRecalculate, textMapToIntentArr } from './utils';
const BASE_HEIGHT = 40;
const WORD_GAP = 0;
const INTENT_HEIGHT = 26;
const Container = styled<'div', {
  $disableSelection: boolean;
}>('div', ({
  $theme,
  $disableSelection
}) => ({
  '*::selection': {
    backgroundColor: '#ffe184'
  },
  borderRadius: '8px',
  padding: '15px 20px',
  height: '100%',
  boxSizing: 'border-box',
  backgroundColor: $theme.colors.backgroundPrimary,
  userSelect: !$disableSelection ? 'initial' : 'none'
}));
const WordContainer = styled('span', ({
  $theme
}) => ({
  marginRight: `${WORD_GAP}px`,
  position: 'relative',
  display: 'inline-block',
  height: `${BASE_HEIGHT}px`,
  padding: '0 2px'
}));
const Word = styled<'span', {
  $highlightColor: RGBColor | undefined | null;
  $bold: boolean;
}>('span', ({
  $highlightColor,
  $bold,
  $theme
}) => ({
  backgroundColor: $highlightColor ? `rgba(${$highlightColor.join(',')}, 0.5)` : 'initial',
  borderRadius: '4px',
  fontWeight: $bold ? 'bold' : 'initial',
  display: 'inline-block',
  padding: '2px 3px',
  textAlign: 'center',
  fontSize: '18px',
  color: $theme.colors.primaryA
}));
const IntentContainer = styled('span', () => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  width: '100%',
  height: `calc(100% - ${BASE_HEIGHT}px)`,
  display: 'flex',
  alignItems: 'center'
}));
const Intent = styled<'span', {
  $top: number;
  $color: RGBColor;
}>('span', ({
  $theme,
  $top,
  $color
}) => ({
  backgroundColor: `rgb(${$color.join(',')})`,
  width: '100%',
  display: 'inline-block',
  height: '4px',
  position: 'absolute',
  left: 0,
  top: `${$top}px`,
  cursor: 'pointer'
}));
const IntentValue = styled<'span', {
  $color: RGBColor;
}>('span', ({
  $color
}) => ({
  backgroundColor: `rgb(${$color.join(',')})`,
  position: 'absolute',
  top: '50%',
  display: 'flex',
  alignItems: 'center',
  fontSize: '12px',
  boxSizing: 'border-box',
  transform: 'translateY(-50%)',
  borderRadius: '2px',
  padding: '4px 6px',
  textTransform: 'uppercase',
  userSelect: 'none',
  letterSpacing: '0.8px',
  color: '#fff',
  zIndex: 2
}));
const DeleteIntent = styled('span', ({
  $theme
}) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  left: '-20px',
  color: $theme.colors.backgroundNegative,
  display: 'flex'
}));
export type LabelledIntentT = {
  value: string;
  startIndex: number;
  endIndex: number;
  color: RGBColor;
  depth: number; // Depth at which this intent belongs(for alignment with siblings);
  selectionCounter: number; // counter to maintain order at which user selected the intent;
};
export type TextIntentT = {
  text: string;
  intent: string;
  startIndex: number;
  endIndex: number;
};
export type GetIntentInfoT = (intentName: string) => {
  color: RGBColor;
  hotkey: string | undefined | null;
};
type PropsT = {
  text: string;
  onUpdate: (intentArr: TextIntentT[]) => void;
  currentIntent?: IntentT | null;
  defaultIntents?: TextIntentT[];
  getIntentInfo: GetIntentInfoT;
  disablePartialLabelling?: boolean;
};
export type TextMap = {
  word: string;
  startCharIndex: number;
  intents: Array<LabelledIntentT | undefined | null>;
};
const getNode = node => {
  // Incase selection returns the parent node
  if (node?.dataset?.id) return node;
  return node.nodeName === '#text' ? node.parentElement.parentElement : node.parentElement;
};
const TextPanel = ({
  text,
  currentIntent,
  onUpdate,
  defaultIntents,
  getIntentInfo,
  disablePartialLabelling
}: PropsT) => {
  const [textMap, setTextMap] = useState<TextMap[]>(() => getInitialTextMap(text, defaultIntents || [], getIntentInfo));
  const [highlightedIntent, setHighlightedIntent] = useState<LabelledIntentT | undefined | null>(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const selectionCounter = useRef(defaultIntents?.length || 0);
  const removeRange = () => {
    document.getSelection().removeAllRanges();
    setSelectedRange(null);
  };
  useEffect(() => {
    if (selectedRange === null || !currentIntent) {
      return;
    }
    // @ts-expect-error missing null check
    const [startIndex, endIndex] = selectedRange;
    const _updatedTextMap = labelText({
      textMap,
      startIndex,
      endIndex,
      currentIntent,
      selectionCounter: selectionCounter.current++
    });

    // eslint-disable-next-line react-hooks/set-state-in-effect
    removeRange();
    if (_updatedTextMap) setTextMap(_updatedTextMap);
  }, [selectedRange, currentIntent, textMap]);
  useEffect(() => {
    if (currentIntent && disablePartialLabelling) {
      // @ts-expect-error missing null check
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRange([0, textMap.length - 1]);
    }
  }, [currentIntent, textMap, disablePartialLabelling]);
  const onSelection = () => {
    const documentSelection = document.getSelection();
    if (documentSelection.type !== 'Range') return;
    const selectedRange = documentSelection.getRangeAt(0);
    if (!selectedRange.collapsed) {
      const startNode = getNode(selectedRange.startContainer);
      const endNode = getNode(selectedRange.endContainer);
      if (!startNode || !endNode) return;
      const [startIndex, endIndex] = [parseInt(startNode.dataset.id, 10), parseInt(endNode.dataset.id, 10)].sort((a, b) => a - b);

      // If endoffset is 0 it means the range ends on the previous word
      // @ts-expect-error missing null check
      setSelectedRange([
      // @ts-expect-error todo(ts-migration) TS2538 Type 'undefined' cannot be used as an index type.
      textMap[startIndex].word.length === selectedRange.startOffset ? startIndex + 1 : startIndex,
      // @ts-expect-error todo(ts-migration) TS18048 'endIndex' is possibly 'undefined'.
      selectedRange.endOffset === 0 ? endIndex - 1 : endIndex]);
    }
  };
  const highlightText = intent => {
    setHighlightedIntent(intent);
  };
  const removeIntent = intent => {
    const _updatedTextMap = removeIntentAndRecalculate([...textMap], intent);
    if (_updatedTextMap !== null) {
      setTextMap(_updatedTextMap);
      setHighlightedIntent(null);
    }
  };
  useEffect(() => {
    onUpdate(textMapToIntentArr(textMap));
  }, [onUpdate, textMap]);
  return <Container onMouseUp={onSelection} $disableSelection={!!disablePartialLabelling}>
      {disablePartialLabelling && <Notification overrides={{
      Body: {
        style: ({
          $theme
        }) => ({
          width: '100%',
          marginBottom: '40px',
          boxSizing: 'border-box'
        })
      }
    }}>
          Partial labelling is disabled for this task. Select intent on the right to label entire
          text
        </Notification>}
      {textMap.map((wordObj, index) => {
      if (wordObj.word === '\\n') {
        // when new line character is encountered return a horizontal line
        return <hr />;
      }
      return <WordContainer key={index} data-id={index} style={{
        height: `${BASE_HEIGHT + wordObj.intents.length * INTENT_HEIGHT}px`
      }}>
            <Word $highlightColor={highlightedIntent && index >= highlightedIntent.startIndex && index <= highlightedIntent.endIndex ? highlightedIntent.color : null} $bold={wordObj.intents.length > 0}>
              {wordObj.word}
            </Word>
            <IntentContainer>
              {wordObj.intents.map((intent, intentIndex) => {
            if (!intent) return null;
            const startingPoint = intent.startIndex === index;
            return <Intent key={intentIndex} $top={intent.depth * INTENT_HEIGHT} $color={intent.color} onMouseEnter={highlightText.bind(null, intent)} onMouseLeave={() => {
              setHighlightedIntent(null);
            }}>
                      {startingPoint ? <>
                          {highlightedIntent && index === highlightedIntent.startIndex && index <= highlightedIntent.endIndex && intent.value === highlightedIntent.value && intent.depth === highlightedIntent.depth && <DeleteIntent onClick={removeIntent.bind(null, intent)}>
                                <XSmall size={22} />
                              </DeleteIntent>}
                          <IntentValue $color={intent.color}>{intent.value}</IntentValue>
                        </> : ''}
                    </Intent>;
          }).filter(intentElem => intentElem !== null)}
            </IntentContainer>
          </WordContainer>;
    })}
    </Container>;
};
export default TextPanel;